// ============================================================================
// Plugin « algèbre » du noyau (lot 2 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
// Paquets : algebra-core@1, equation-core@1, inequality-core@1, explog-core@1,
// radical-core@1, counting-core@1 (moteur v27), log-core@1 (moteur v47), condition-core@1
// (moteur v49) (JSON déclaratifs,
// validés et chargés par le registre). Un vérificateur : `checkAlgebraStep`.
// Rien n'est branché à l'app (lot 4).
// ============================================================================

import type { KernelPlugin } from '../../registry';
import { statementSymbols, parseStatement, type Sorts } from '../../term';
import { unsupportedCheck, type StepInput } from '../../types';
import { checkAlgebraStep } from './check';
import algebraCore from './packs/algebra-core.json';
import conditionCore from './packs/condition-core.json';
import countingCore from './packs/counting-core.json';
import equationCore from './packs/equation-core.json';
import explogCore from './packs/explog-core.json';
import logCore from './packs/log-core.json';
import radicalCore from './packs/radical-core.json';
import inequalityCore from './packs/inequality-core.json';

export {
  checkAlgebraStep,
  checkAlgebraStatements,
  checkApproximation,
  type AlgebraCheckContext,
  type AngleDomain,
} from './check';
export const ALGEBRA_PACKS: readonly unknown[] = [
  algebraCore,
  equationCore,
  inequalityCore,
  explogCore,
  radicalCore,
  countingCore,
  logCore,
  conditionCore,
];

/** Matrices : toutes les lettres de l'étape sont des matrices. */
function sortsFor(input: StepInput): Sorts {
  if (input.sort !== 'matrix') return {};
  const out: Record<string, 'matrix'> = {};
  for (const text of [input.from, input.to]) {
    const p = parseStatement(text);
    if (p.ok) for (const n of statementSymbols(p.s)) out[n] = 'matrix';
  }
  return out;
}

export function createAlgebraPlugin(): KernelPlugin {
  return {
    id: 'algebra',
    version: 1,
    sorts: ['real', 'matrix'],
    rulePacks: ALGEBRA_PACKS,
    goals: ['factor', 'expand', 'simplify', 'solve', 'isolate_variable', 'compute'],
    limits: { ms: 50, nodes: 20_000, depth: 8, branches: 4 },
    verifiers: [
      {
        id: 'algebra-step',
        check(input, ctx) {
          if (input.sort !== 'real' && input.sort !== 'matrix')
            return unsupportedCheck('sort_not_supported', { sort: input.sort });
          if (ctx.now() > ctx.deadline) return unsupportedCheck('budget_exhausted');
          return checkAlgebraStep(input.from, input.to, {
            rules: ctx.rules,
            hypotheses: input.hypotheses,
            goal: input.goal,
            sorts: sortsFor(input),
            deadline: ctx.deadline,
            now: ctx.now,
            fuel: Math.min(ctx.budget.nodes, 20_000),
            branches: ctx.budget.branches > 0,
          });
        },
      },
    ],
    conformance: [
      {
        name: '11.1 soustraire 5',
        input: { sort: 'real', from: '12x + 5 = 14', to: '12x = 9' },
        expect: 'valid',
      },
      {
        name: '11.1 diviser par 12',
        input: { sort: 'real', from: '12x = 9', to: 'x = 3 ÷ 4' },
        expect: 'valid',
      },
      {
        name: '11.2 factoriser',
        input: { sort: 'real', from: 'x² − 9', to: '(x − 3)(x + 3)' },
        expect: 'valid',
      },
      {
        name: '11.2 développer',
        input: { sort: 'real', from: '(x − 3)(x + 3)', to: 'x² − 9' },
        expect: 'valid',
      },
      {
        name: '11.3 multiplier par −1',
        input: { sort: 'real', from: '−x < −3', to: 'x > 3' },
        expect: 'valid',
      },
      {
        name: '11.3 sens non changé',
        input: { sort: 'real', from: '−x < −3', to: 'x < 3' },
        expect: 'invalid',
      },
      {
        name: '11.4 symétrie de =',
        input: { sort: 'real', from: 'a = b', to: 'b = a' },
        expect: 'valid',
      },
      {
        name: '11.4 pas de symétrie de <',
        input: { sort: 'real', from: 'a < b', to: 'b < a' },
        expect: 'invalid',
      },
      {
        name: '11.5 somme commutative',
        input: { sort: 'real', from: 'a + b', to: 'b + a' },
        expect: 'valid',
      },
      {
        name: '11.5 AB ≠ BA',
        input: { sort: 'matrix', from: 'A × B', to: 'B × A' },
        expect: 'invalid',
      },
      {
        name: '11.9 √2 ≠ 1,414',
        input: { sort: 'real', from: '√2', to: '1,414' },
        expect: 'invalid',
      },
      {
        name: 'division par zéro',
        input: { sort: 'real', from: 'x = 3', to: 'x ÷ 0 = 3' },
        expect: 'invalid',
      },
      {
        name: 'solution perdue',
        input: { sort: 'real', from: 'x² = 9', to: 'x = 3' },
        expect: 'invalid',
      },
      {
        name: 'signe inconnu',
        input: { sort: 'real', from: 'a × x < a × 3', to: 'x > 3' },
        expect: 'conditional',
      },
    ],
  };
}
